import { debounce } from "es-toolkit/compat";
import type { InstantSearchOptions } from "instantsearch.js";
import { useEffect, useState } from "react";
import type { UseSearchBoxProps } from "react-instantsearch";
import type { ShouldRevalidateFunction } from "react-router-dom";

import { ALGOLIA_PRODUCT_TYPES } from "@/utils/algoliaCatalog";
import type { Models } from "api/types";

/** How long typing pauses before the query is sent: Algolia bills every request, not every search a patient meant. */
export const SEARCH_DEBOUNCE_MS = 300;

/** A `useSearchBox` query hook that sends only the query typing settled on, one per mounted search box. */
export const useDebouncedQueryHook = (): NonNullable<UseSearchBoxProps["queryHook"]> => {
  const [queryHook] = useState(() =>
    debounce((query: string, search: (value: string) => void) => search(query), SEARCH_DEBOUNCE_MS),
  );
  // The debounce timer outlives the component: a query still pending at unmount would refine the disposed
  // InstantSearch and throw, and no event handler runs on unmount to cancel it.
  useEffect(() => () => queryHook.cancel(), [queryHook]);
  return queryHook;
};

/** An empty search box shows nothing, so it asks Algolia nothing. */
export const searchOnlyWithQuery: NonNullable<InstantSearchOptions["searchFunction"]> = (
  helper,
) => {
  if (helper.state.query) helper.search();
};

/** The search page mirrors its query to `?q=`; that alone changes nothing the loader reads. */
export const shouldRevalidateSearch: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  const withoutQuery = (url: URL) => {
    const params = new URLSearchParams(url.search);
    params.delete("q");
    return `${url.pathname}?${params.toString()}`;
  };
  const onlyQueryChanged =
    currentUrl.searchParams.get("q") !== nextUrl.searchParams.get("q") &&
    withoutQuery(currentUrl) === withoutQuery(nextUrl);
  return onlyQueryChanged ? false : defaultShouldRevalidate;
};

export const searchProductTypes = (
  platformSettings: Models<"PlatformModel">["platformSettings"],
  isAnonymousBrowsing: boolean,
): string[] =>
  Object.values(ALGOLIA_PRODUCT_TYPES).filter((type) => {
    switch (type) {
      case ALGOLIA_PRODUCT_TYPES.treatment:
      case ALGOLIA_PRODUCT_TYPES.package:
        return platformSettings?.treatmentsAndPackagesEnabled !== false;
      case ALGOLIA_PRODUCT_TYPES.giftCard:
        return !!platformSettings?.giftCardEnabled && !isAnonymousBrowsing;
      case ALGOLIA_PRODUCT_TYPES.membership:
        return platformSettings?.membershipsEnabled !== false;
      default:
        return true;
    }
  });
